import {
  Transaction,
  TransactionArgument,
  TransactionObjectInput,
  TransactionResult,
} from '@mysten/sui/transactions'
import type { EnvConfig } from '../../_envs'
import { getPublishedAt } from '../../_envs'
import { obj, pure } from '../../_framework/util'

export function zero(tx: Transaction, options?: { env?: EnvConfig }): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::zero`,
    arguments: [],
  })
}

export function fromU32(
  tx: Transaction,
  v: number | TransactionArgument,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::from_u32`,
    arguments: [pure(tx, v, `u32`)],
  })
}

export function from(
  tx: Transaction,
  v: number | TransactionArgument,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::from`,
    arguments: [pure(tx, v, `u32`)],
  })
}

export function negFrom(
  tx: Transaction,
  v: number | TransactionArgument,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::neg_from`,
    arguments: [pure(tx, v, `u32`)],
  })
}

export interface WrappingAddArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function wrappingAdd(
  tx: Transaction,
  args: WrappingAddArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::wrapping_add`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface AddArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function add(
  tx: Transaction,
  args: AddArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::add`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface WrappingSubArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function wrappingSub(
  tx: Transaction,
  args: WrappingSubArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::wrapping_sub`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface SubArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function sub(
  tx: Transaction,
  args: SubArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::sub`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface MulArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function mul(
  tx: Transaction,
  args: MulArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::mul`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface DivArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function div(
  tx: Transaction,
  args: DivArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::div`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export function abs(
  tx: Transaction,
  v: TransactionObjectInput,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::abs`,
    arguments: [obj(tx, v)],
  })
}

export function absU32(
  tx: Transaction,
  v: TransactionObjectInput,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::abs_u32`,
    arguments: [obj(tx, v)],
  })
}

export interface ShlArgs {
  v: TransactionObjectInput
  shift: number | TransactionArgument
}

export function shl(
  tx: Transaction,
  args: ShlArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::shl`,
    arguments: [
      obj(tx, args.v),
      pure(tx, args.shift, `u8`),
    ],
  })
}

export interface ShrArgs {
  v: TransactionObjectInput
  shift: number | TransactionArgument
}

export function shr(
  tx: Transaction,
  args: ShrArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::shr`,
    arguments: [
      obj(tx, args.v),
      pure(tx, args.shift, `u8`),
    ],
  })
}

export interface ModArgs {
  v: TransactionObjectInput
  n: TransactionObjectInput
}

export function mod(
  tx: Transaction,
  args: ModArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::mod`,
    arguments: [
      obj(tx, args.v),
      obj(tx, args.n),
    ],
  })
}

export function asU32(
  tx: Transaction,
  v: TransactionObjectInput,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::as_u32`,
    arguments: [obj(tx, v)],
  })
}

export function sign(
  tx: Transaction,
  v: TransactionObjectInput,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::sign`,
    arguments: [obj(tx, v)],
  })
}

export function isNeg(
  tx: Transaction,
  v: TransactionObjectInput,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::is_neg`,
    arguments: [obj(tx, v)],
  })
}

export interface CmpArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function cmp(
  tx: Transaction,
  args: CmpArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::cmp`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface EqArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function eq(
  tx: Transaction,
  args: EqArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::eq`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface GtArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function gt(
  tx: Transaction,
  args: GtArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::gt`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface GteArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function gte(
  tx: Transaction,
  args: GteArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::gte`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface LtArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function lt(
  tx: Transaction,
  args: LtArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::lt`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface LteArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function lte(
  tx: Transaction,
  args: LteArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::lte`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface OrArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function or(
  tx: Transaction,
  args: OrArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::or`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export interface AndArgs {
  num1: TransactionObjectInput
  num2: TransactionObjectInput
}

export function and(
  tx: Transaction,
  args: AndArgs,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::and`,
    arguments: [
      obj(tx, args.num1),
      obj(tx, args.num2),
    ],
  })
}

export function u32Neg(
  tx: Transaction,
  v: number | TransactionArgument,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::u32_neg`,
    arguments: [pure(tx, v, `u32`)],
  })
}

export function u8Neg(
  tx: Transaction,
  v: number | TransactionArgument,
  options?: { env?: EnvConfig },
): TransactionResult {
  return tx.moveCall({
    target: `${getPublishedAt('integer-mate', options?.env)}::i32::u8_neg`,
    arguments: [pure(tx, v, `u8`)],
  })
}
